import { anchored } from '../util/viewportAnchor.js';
/**
 * Rex Mortis — Events / Annual Calendar page
 *
 * Lists every catalog event for the current year with start / end dates and a
 * Claim button on whichever ones are currently active. Players claim once per
 * event to earn the participation title + a small crown/boon reward.
 */
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { formatCrowns } from '../util/formatBig.js';

type EventRow = Awaited<ReturnType<typeof api.listEvents>>['events'][number];

interface Flash {
  kind: 'success' | 'danger';
  message: string;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const CATEGORY_LABEL: Record<string, string> = {
  tourney: 'Tourney',
  festival: 'Festival',
  fair: 'Fair',
  pilgrimage: 'Pilgrimage',
  war: 'War',
};

export async function renderEvents(root: HTMLElement): Promise<void> {
  let events: EventRow[] = [];
  let flash: Flash | null = null;
  let compline: Awaited<ReturnType<typeof api.complineStatus>> | null = null;

  async function refresh(): Promise<void> {
    try {
      const r = await api.listEvents();
      events = r.events;
    } catch (err) {
      flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Could not load events.' };
    }
    try {
      compline = await api.complineStatus();
    } catch {
      compline = null;
    }
  }

  await refresh();

  mountShell(root, (main) => {
    const renderMain = (): void => {
      const active = events.filter((e) => e.active);
      const upcoming = events.filter((e) => !e.active && new Date(e.startsAt).getTime() > Date.now());
      const past = events.filter((e) => !e.active && new Date(e.endsAt).getTime() <= Date.now());

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/events.webp" alt="" />
        <h1>The Calendar of Realms</h1>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
          Annual festivals, tourneys and pilgrimages. Take part during the window to earn a title.
        </p>

        ${compline ? `<div class="panel" style="margin: var(--s-3) 0;">
          <strong style="font-family: var(--font-elegant);">The Bell of Compline</strong>
          <p style="margin: 4px 0 0 0; font-size: 0.9rem; line-height: 1.4;">
            Each evening the bell tolls and a blessing falls for one hour. ${
              compline.active
                ? `It rings now — <strong>${compline.active.label}</strong> (${compline.active.blurb} +${compline.active.bonusPct}%).`
                : `Next toll: <strong>${new Date(compline.nextTollAt).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}</strong> (your local time).`
            }
          </p>
        </div>` : ''}

        ${flash ? `<div class="banner banner-${flash.kind}">${flash.message}</div>` : ''}

        <h2 style="margin-top: 1.5rem;">Now</h2>
        ${active.length === 0 ? '<p class="caption">No event running today. Check the calendar below.</p>' : ''}
        ${active.map(renderCard).join('')}

        <h2 style="margin-top: 2rem;">Upcoming</h2>
        ${upcoming.length === 0 ? '<p class="caption">All scheduled events have run this year.</p>' : ''}
        <div class="card-grid">${upcoming.map(renderCard).join('')}</div>

        <h2 style="margin-top: 2rem;">Past</h2>
        <div class="card-grid">${past.map(renderCard).join('')}</div>
      `;

      // Wire claim buttons.
      main.querySelectorAll<HTMLButtonElement>('button[data-claim-id]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset.claimId!;
          btn.disabled = true;
          btn.textContent = 'Claiming…';
          try {
            const r = await api.claimEvent(id);
            flash = {
              kind: 'success',
              message: `Claimed! You earned the "${r.titleAwarded}" title, ${formatCrowns(r.crownsPaid)} crowns${r.boonsPaid > 0 ? `, and ${r.boonsPaid} boon${r.boonsPaid > 1 ? 's' : ''}` : ''}.`,
            };
            await refresh();
          } catch (err) {
            flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Could not claim.' };
          }
          anchored(renderMain);
        });
      });
    };

    function renderCard(e: EventRow): string {
      const cat = CATEGORY_LABEL[e.category] ?? e.category;
      const dateRange = `${fmtDate(e.startsAt)} – ${fmtDate(e.endsAt)}`;
      const reward = `${formatCrowns(e.crownReward)}c${e.boonReward > 0 ? ` + ${e.boonReward} boon${e.boonReward > 1 ? 's' : ''}` : ''} + title`;
      const action = e.claimed
        ? `<span class="badge-gold">Claimed</span>`
        : e.active
          ? `<button class="btn btn-primary" data-claim-id="${e.id}">Claim Participation</button>`
          : `<span class="caption">${dateRange}</span>`;
      return `
        <article class="card ${e.active ? 'card-active' : ''}" style="margin-top: 0.75rem;">
          <div style="display:flex; justify-content:space-between; align-items:baseline;">
            <h3 style="margin:0;">${e.name}</h3>
            <span class="caption">${cat} · ${dateRange}</span>
          </div>
          <p style="margin: 0.5rem 0;">${e.blurb}</p>
          <div style="display:flex; justify-content:space-between; align-items:center; gap:0.75rem; flex-wrap:wrap;">
            <span class="caption">Reward: ${reward}</span>
            ${action}
          </div>
        </article>
      `;
    }

    anchored(renderMain);
  });
}
